/**
 * VerseActionSheet - Bottom sheet for verse actions.
 * Shows options to highlight, add note, bookmark, copy, share.
 */
import { useState, useEffect } from 'react';
import { X, HelpCircle, ChevronRight, Languages, Users, SquarePen, Copy, Share2 } from 'lucide-react';
import type { BibleVerse, BibleNote } from '../types';
import { HIGHLIGHT_COLORS, HIGHLIGHT_COLOR_NAMES } from '../utils';

const HIGHLIGHT_COLOR_LIST = HIGHLIGHT_COLOR_NAMES.map(name => ({
  name,
  bg: HIGHLIGHT_COLORS[name].bg,
  border: HIGHLIGHT_COLORS[name].border,
}));

interface VerseActionSheetProps {
  verse: BibleVerse;
  reference: string; // e.g., "John 3:16"
  note: BibleNote | null;
  isOpen: boolean;
  onClose: () => void;
  onHighlight: (color: string) => void;
  onRemoveHighlight: () => void;
  onAddNote: () => void;
  onEditNote: () => void;
  onCopy: () => void;
  onShare: () => void;
  onAsk?: () => void;
  onWordStudy?: () => void;
  onViewCommunityNotes?: () => void;
}

export default function VerseActionSheet({
  verse,
  reference,
  note,
  isOpen,
  onClose,
  onHighlight,
  onRemoveHighlight,
  onAddNote,
  onEditNote,
  onCopy,
  onShare,
  onAsk,
  onWordStudy,
  onViewCommunityNotes,
}: VerseActionSheetProps) {
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);

  // Lock body scroll when sheet is open (without breaking touch events inside)
  useEffect(() => {
    if (isOpen) {
      // Store current scroll position
      const scrollY = window.scrollY;
      // Lock body
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore scroll position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleNoteAction = () => {
    if (note || verse.has_note) {
      onEditNote();
    } else {
      onAddNote();
    }
  };

  const handleHighlightClick = (color: string) => {
    // If clicking the same color that's already highlighted, show remove option
    if (verse.is_highlighted && verse.highlight_color === color) {
      setShowConfirmRemove(true);
    } else {
      onHighlight(color);
      onClose();
    }
  };

  const handleRemoveHighlight = () => {
    onRemoveHighlight();
    setShowConfirmRemove(false);
    onClose();
  };

  const handleCopy = () => {
    onCopy();
    onClose();
  };

  const handleShare = () => {
    onShare();
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="fixed bottom-0 left-0 right-0 bg-surface rounded-t-2xl z-50 animate-slide-up safe-bottom">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-border rounded-full" />
        </div>

        {/* Verse preview */}
        <div className="px-4 pb-3 border-b border-border">
          <p className="text-xs text-text-secondary font-medium">{reference}</p>
          <p className="text-sm text-text line-clamp-2 mt-1">{verse.text}</p>
        </div>

        {/* Highlight colors */}
        <div className="px-4 py-4 border-b border-border">
          <p className="text-xs text-text-secondary mb-3 font-medium">Highlight</p>
          <div className="flex gap-3 justify-center">
            {HIGHLIGHT_COLOR_LIST.map((color) => {
              const isSelected = verse.is_highlighted && verse.highlight_color === color.name;
              return (
                <button
                  key={color.name}
                  onClick={() => handleHighlightClick(color.name)}
                  className={`w-10 h-10 rounded-full ${color.bg} transition-all ${
                    isSelected
                      ? `ring-2 ring-offset-2 ${color.border} ring-current scale-110`
                      : 'hover:scale-110'
                  }`}
                  aria-label={`Highlight ${color.name}${isSelected ? ' (selected)' : ''}`}
                />
              );
            })}
            {/* Remove highlight button */}
            {verse.is_highlighted && (
              <button
                onClick={() => setShowConfirmRemove(true)}
                className="w-10 h-10 rounded-full border-2 border-dashed border-border flex items-center justify-center text-text-muted hover:text-error hover:border-error transition-colors"
                aria-label="Remove highlight"
              >
                <X size={20} />
              </button>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="px-4 py-2">
          {/* Ask button */}
          {onAsk && (
            <button
              onClick={() => { onAsk(); onClose(); }}
              className="w-full flex items-center gap-3 py-3 hover:bg-surface-hover rounded-lg transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <HelpCircle size={20} className="text-primary" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-text font-medium">Ask About This Verse</p>
                <p className="text-xs text-text-secondary">Get insights and explanations</p>
              </div>
              <ChevronRight size={20} className="text-text-muted" />
            </button>
          )}

          {/* Word Study button */}
          {onWordStudy && (
            <button
              onClick={onWordStudy}
              className="w-full flex items-center gap-3 py-3 hover:bg-surface-hover rounded-lg transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Languages size={20} className="text-primary" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-text font-medium">Word Study</p>
                <p className="text-xs text-text-secondary">See original Hebrew/Greek meanings</p>
              </div>
              <ChevronRight size={20} className="text-text-muted" />
            </button>
          )}

          {/* Community Notes button */}
          {onViewCommunityNotes && verse.community_notes_count > 0 && (
            <button
              onClick={onViewCommunityNotes}
              className="w-full flex items-center gap-3 py-3 hover:bg-surface-hover rounded-lg transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Users size={20} className="text-primary" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-text font-medium">Community Notes</p>
                <p className="text-xs text-text-secondary">{verse.community_notes_count} {verse.community_notes_count === 1 ? 'note' : 'notes'} from friends</p>
              </div>
              <ChevronRight size={20} className="text-text-muted" />
            </button>
          )}

          {/* Note button */}
          <button
            onClick={handleNoteAction}
            className="w-full flex items-center gap-3 py-3 hover:bg-surface-hover rounded-lg transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <SquarePen size={20} className="text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-text font-medium">
                {note || verse.has_note ? 'Edit Note' : 'Add Note'}
              </p>
              {(note || verse.has_note) && (
                <p className="text-xs text-text-secondary line-clamp-1">
                  {note?.content || 'View your note'}
                </p>
              )}
            </div>
            <ChevronRight size={20} className="text-text-muted" />
          </button>


          {/* Copy button */}
          <button
            onClick={handleCopy}
            className="w-full flex items-center gap-3 py-3 hover:bg-surface-hover rounded-lg transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Copy size={20} className="text-primary" />
            </div>
            <span className="text-text font-medium">Copy Verse</span>
          </button>

          {/* Share button */}
          <button
            onClick={handleShare}
            className="w-full flex items-center gap-3 py-3 hover:bg-surface-hover rounded-lg transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Share2 size={20} className="text-primary" />
            </div>
            <span className="text-text font-medium">Share</span>
          </button>
        </div>

        {/* Bottom padding */}
        <div className="h-4" />
      </div>

      {/* Confirm remove highlight dialog */}
      {showConfirmRemove && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowConfirmRemove(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-surface rounded-2xl p-6 z-50 w-[90%] max-w-sm">
            <h3 className="text-lg font-semibold text-text mb-2">Remove Highlight?</h3>
            <p className="text-text-secondary text-sm mb-4">
              This will remove the highlight from this verse.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmRemove(false)}
                className="flex-1 py-2 rounded-lg border border-border text-text font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveHighlight}
                className="flex-1 py-2 rounded-lg bg-error text-white font-medium"
              >
                Remove
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
